<template>
  <fieldset class="max-w-4xl">
    <legend class="hidden">{{ label }}</legend>
    <div v-for="(option, index) in options" :key="index">

      <label class="container" :for="`${name}-${option.value}`">
        <input
          type="checkbox"
          :id="`${name}-${option.value}`"
          :name="name"
          :value="option.value"
          :checked="isChecked === true"
          v-validate="checkboxRequired"
          data-vv-as="checkbox"
          :aria-describedby="generateAriaDescription(name)"
          @change="emitCheckbox($event)"
        />
        <span class="checkmark"></span>
        <span v-html="findUrls(option.text)"></span>
      </label>
    </div>
    <doe-error v-if="errors.has(name)" :description="ariaErrorDescID(name)">{{ errors.first(name) }}</doe-error>
    <doe-hint :hint="hint" :description="ariaDescID(name)" />
  </fieldset>
</template>

<script>
import dynamicValues from "@/mixins/dynamicValues";
import DoeHint from "@/components/field/Hint";
import DoeError from "@/components/field/Error";

export default {
  name: "DoeCheckbox",
  mixins: [dynamicValues],
  inject: {
    $validator: "$validator"
  },
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: [Boolean, Number, String]
    },
    options: {
      type: Array,
      required: true
    },
    label: {
      type: String
    },
    validation: {
      required: false
    },
    hint: {
      type: [Array, String]
    }
  },
  components: {
    DoeHint,
    DoeError
  },
  data() {
    return {
      isChecked: false
    };
  },
  methods: {
    findUrls(text) {
      return text.replace(
        /[[]{1}([^\]]+)[\]]{1}[(]{1}([^)"]+)("(.+)")?[)]{1}/g,
        '<a href="$2" target="_blank">$1<span class="sr-only">Opens external website</span></a>'
      );
    },
    generateAriaDescription(fieldName) {
      if (this.errors.has(fieldName)) {
        return this.ariaErrorDescID(fieldName);
      }
      if (this.hint) {
        return this.ariaDescID(fieldName);
      }
      return false;
    },
    ariaDescID(fieldName) {
      return `${fieldName}Description`;
    },
    ariaErrorDescID(fieldName) {
      return `${fieldName}ErrorDescription`;
    },
    emitCheckbox($event) {
      if ($event.target.checked) {
        this.isChecked = $event.target.value;
      } else {
        this.isChecked = false;
      }
      this.$emit("input", this.isChecked);
    }
  },
  computed: {
    checkboxRequired() {
      return this.getRequiredValue(this.validation);
    }
  }
};
</script>

<style lang="scss" scoped>
fieldset {
  @apply w-full;
}
legend {
  @apply font-light font-sans leading-snug max-w-3xl mb-3 subpixel-antialiased text-xl text-black;
}
label {
  @apply relative cursor-pointer font-sans mb-4 pl-6 text-base text-gray-900 leading-loose inline-block subpixel-antialiased w-full;
}
input {
  @apply absolute overflow-hidden h-0 w-0;
  clip: rect(1px, 1px, 1px, 1px);
}
input + label:before {
  @apply absolute bg-transparent block border border-2 border-blue-900 rounded;
  content: " ";
  top: 0px;
  left: 0px;
  width: 1.5rem;
  height: 1.5rem;
}
.container {
  display: block;
  position: relative;
  padding-left: 35px;
  margin-bottom: 12px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 1.5rem;
  width: 1.5rem;
  background-color: transparent;
  border-radius: 0.25rem;
  border-width: 2px;
  border-color: #041e42;
  border-style: solid;
}

/* On mouse-over, add a grey background color */
.checkmark:hover {
  background-color: #ccc;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  /*background-color: #2196F3;*/
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 6px;
  top: 1px;
  width: 8px;
  height: 16px;
  border: solid #041e42;
  border-width: 0 4px 4px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

input:focus + span.checkmark {
  @apply border-blue-900;
  box-shadow: 0 0 0 1px #041e42;
  outline: 1px solid #041e42;
  outline-offset: 3px;
}
span /deep/ a {
  @apply inline-block relative underline;
}
span /deep/ a[href*="//"]:not([href*="localhost.com"]) {
  padding-right: 1.5rem;
  &:after {
    @apply absolute block;
    content: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 100 89'><path d='M100,4.16666667 L100,26.3861111 C100,30.1119792 95.4930556,31.9381944 92.8869792,29.3324653 L86.6878472,23.1329861 L44.4095486,65.4112847 C42.7822917,67.0385417 40.1442708,67.0385417 38.5170139,65.4112847 L34.5887153,61.4829861 C32.9614583,59.8557292 32.9614583,57.2175347 34.5887153,55.5904514 L76.8673611,13.3118056 L70.6689236,7.11302083 C68.0524306,4.49652778 69.9055556,0 73.6152778,0 L95.8333333,0 C98.1345486,0 100,1.86545139 100,4.16666667 Z M70.6647569,47.0128472 L67.8869792,49.790625 C67.1055707,50.5720468 66.6666073,51.6318913 66.6666667,52.7369792 L66.6666667,77.7777778 L11.1111111,77.7777778 L11.1111111,22.2222222 L56.9444444,22.2222222 C58.049457,22.2221592 59.1092025,21.783211 59.890625,21.0019097 L62.6684028,18.2241319 C65.2932292,15.5991319 63.4342014,11.1111111 59.7222222,11.1111111 L8.33333333,11.1111111 C3.73090278,11.1111111 0,14.8420139 0,19.4444444 L0,80.5555556 C0,85.1579861 3.73090278,88.8888889 8.33333333,88.8888889 L69.4444444,88.8888889 C74.046875,88.8888889 77.7777778,85.1579861 77.7777778,80.5555556 L77.7777778,49.9590278 C77.7777778,46.246875 73.2895833,44.3878472 70.6647569,47.0128472 Z' /></svg>");
    top: 0px;
    right: 0px;
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
